import React from 'react';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-page">
      <div className="container">
        <div className="privacy-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: June 2025</p>
          
          <section>
            <h2>1. Introduction</h2>
            <p>
              KenkoSnap ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application ("App"). Please read this privacy policy carefully.
            </p>
            <p>
              By using KenkoSnap, you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>Personal Information</h3>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, age, gender, height, weight</li>
              <li><strong>Health Data:</strong> Body weight, body fat percentage, health goals, dietary preferences</li>
              <li><strong>Food Photos:</strong> Images of meals you upload for nutritional analysis</li>
              <li><strong>Usage Data:</strong> App interaction data, feature usage patterns, session duration</li>
              <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li>Log data (IP address, browser type, pages visited)</li>
              <li>Device characteristics and performance data</li>
              <li>App usage analytics and crash reports</li>
              <li>Location data (only if you enable location services)</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            
            <h3>Primary App Functions</h3>
            <ul>
              <li>Analyze food photos using AI to provide nutritional information</li>
              <li>Track your health metrics and progress over time</li>
              <li>Provide personalized health insights and recommendations</li>
              <li>Calculate and display your daily nutritional intake</li>
              <li>Generate progress reports and visualizations</li>
            </ul>

            <h3>Service Improvement</h3>
            <ul>
              <li>Improve our AI algorithms and food recognition accuracy</li>
              <li>Enhance app performance and user experience</li>
              <li>Develop new features and functionality</li>
              <li>Conduct research and analytics (using aggregated, anonymized data)</li>
            </ul>

            <h3>Communication</h3>
            <ul>
              <li>Send you important app updates and security notices</li>
              <li>Respond to your customer support requests</li>
              <li>Send promotional content (only with your consent)</li>
            </ul>
          </section>

          <section>
            <h2>4. Health Information Privacy</h2>
            <p>
              <strong>Your health data is especially sensitive, and we treat it with the highest level of protection:</strong>
            </p>
            <ul>
              <li>Health data is encrypted both in transit and at rest</li>
              <li>We comply with applicable health data protection laws (HIPAA, where applicable)</li>
              <li>Health information is never sold to third parties</li>
              <li>Only aggregated, anonymized health data may be used for research purposes</li>
              <li>You can delete your health data at any time through the app settings</li>
              <li>We integrate with Apple HealthKit only with your explicit permission</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Sharing and Disclosure</h2>
            <p>We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:</p>
            
            <h3>Service Providers</h3>
            <ul>
              <li>Cloud storage providers (for secure data backup)</li>
              <li>AI/ML service providers (for food recognition processing)</li>
              <li>Analytics providers (with anonymized data only)</li>
              <li>Customer support platforms</li>
            </ul>

            <h3>Legal Requirements</h3>
            <ul>
              <li>When required by law or legal process</li>
              <li>To protect our rights, property, or safety</li>
              <li>To protect the rights, property, or safety of our users</li>
              <li>In connection with a merger, acquisition, or sale of assets</li>
            </ul>

            <h3>With Your Consent</h3>
            <ul>
              <li>When you explicitly authorize us to share specific information</li>
              <li>For features that require third-party integration (e.g., social sharing)</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Security</h2>
            <p>We implement industry-standard security measures to protect your information:</p>
            <ul>
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
              <li><strong>Access Controls:</strong> Strict employee access controls and authentication</li>
              <li><strong>Regular Audits:</strong> Security assessments and vulnerability testing</li>
              <li><strong>Secure Infrastructure:</strong> Data stored on secure, compliant cloud platforms</li>
              <li><strong>Data Minimization:</strong> We collect only necessary information</li>
              <li><strong>Anonymization:</strong> Personal identifiers removed from research data</li>
            </ul>
          </section>

          <section>
            <h2>7. Your Privacy Rights</h2>
            <p>You have the following rights regarding your personal information:</p>
            
            <h3>Access and Portability</h3>
            <ul>
              <li>Request a copy of your personal data</li>
              <li>Export your health data in a portable format</li>
              <li>View what information we have collected about you</li>
            </ul>

            <h3>Correction and Deletion</h3>
            <ul>
              <li>Correct inaccurate personal information</li>
              <li>Delete your account and associated data</li>
              <li>Request deletion of specific data categories</li>
            </ul>

            <h3>Control and Consent</h3>
            <ul>
              <li>Opt-out of promotional communications</li>
              <li>Withdraw consent for optional data processing</li>
              <li>Control data sharing with third-party services</li>
              <li>Manage HealthKit integration permissions</li>
            </ul>
          </section>

          <section>
            <h2>8. Children's Privacy</h2>
            <p>
              KenkoSnap is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
            <p>
              For users between 13-18 years old, we recommend parental guidance when using health tracking applications.
            </p>
          </section>

          <section>
            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards, including:
            </p>
            <ul>
              <li>Standard Contractual Clauses (SCCs) for EU data transfers</li>
              <li>Adequacy decisions by relevant data protection authorities</li>
              <li>Other legally recognized transfer mechanisms</li>
            </ul>
          </section>

          <section>
            <h2>10. Data Retention</h2>
            <p>We retain your information for as long as necessary to provide our services and comply with legal obligations:</p>
            <ul>
              <li><strong>Account Data:</strong> Until you delete your account</li>
              <li><strong>Health Data:</strong> Until you delete it or close your account</li>
              <li><strong>Usage Data:</strong> Up to 2 years for analytics purposes</li>
              <li><strong>Support Communications:</strong> Up to 3 years</li>
              <li><strong>Legal Compliance:</strong> As required by applicable laws</li>
            </ul>
            <p>When data is deleted, it is permanently removed from our active systems within 30 days.</p>
          </section>

          <section>
            <h2>11. Third-Party Services</h2>
            <p>KenkoSnap may integrate with third-party services. These integrations are optional and require your explicit consent:</p>
            <ul>
              <li><strong>Apple HealthKit:</strong> To sync health data with Apple Health app</li>
              <li><strong>Analytics Services:</strong> To improve app performance (anonymized data only)</li>
              <li><strong>Cloud Storage:</strong> To securely backup your data</li>
              <li><strong>AI Services:</strong> To process food images for nutritional analysis</li>
            </ul>
            <p>Each third-party service has its own privacy policy, which we encourage you to review.</p>
          </section>

          <section>
            <h2>12. California Privacy Rights (CCPA)</h2>
            <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act:</p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
            <p>To exercise these rights, contact us at privacy@kenkosnap.com</p>
          </section>

          <section>
            <h2>13. European Privacy Rights (GDPR)</h2>
            <p>If you are in the European Economic Area, you have rights under the General Data Protection Regulation:</p>
            <ul>
              <li>Right of access to your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
            <p>To exercise these rights or file a complaint with a supervisory authority, contact us at privacy@kenkosnap.com</p>
          </section>

          <section>
            <h2>14. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </p>
            <ul>
              <li>Posting the new Privacy Policy in the app</li>
              <li>Sending you an email notification</li>
              <li>Displaying a prominent notice in the app</li>
              <li>Updating the "Last updated" date at the top of this policy</li>
            </ul>
            <p>Your continued use of the app after changes become effective constitutes acceptance of the revised Privacy Policy.</p>
          </section>

          <section>
            <h2>15. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> privacy@kenkosnap.com</p>
              <p><strong>Website:</strong> https://kenkosnap.com</p>
              <p><strong>Data Protection Officer:</strong> dpo@kenkosnap.com</p>
              <p><strong>Response Time:</strong> We will respond to privacy inquiries within 30 days</p>
            </div>
          </section>

          <div className="back-link">
            <a href="/">← Back to KenkoSnap</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy; 